import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AssetCategorization, {
	mapCategoryName,
} from "../data/asset-categorization";
import { AssetCategory } from "velavu-js-api";
import IconAdd from "../dynamicicons/icon-add";
import IconArrowForward from "../dynamicicons/icon-arrow-forward";
import IconClose from "../dynamicicons/icon-close";
import { CategoryTag, GroupTag } from "./categorization-tag";
import InfoTooltip from "./info-tooltip";
import VelavuAutocomplete, {
	AutocompleteDivider,
	AutocompleteEntry,
} from "./velavu-autocomplete";
import * as styles from "./velavu-categorization-autocomplete.module.scss";
import whitelabel from "../whitelabel";

interface AutocompleteInputProps {
	value?: AssetCategorization;
	onChange: (value?: AssetCategorization) => void;
	categorization: AssetCategorization[];
	disabled?: boolean;

	containerProps?: React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	>;
	inputProps?: React.DetailedHTMLProps<
		React.InputHTMLAttributes<HTMLInputElement>,
		HTMLInputElement
	>;
}

const autocompleteLimit = 3;
export default function CategorizationAutocomplete(
	props: AutocompleteInputProps,
) {
	const { t } = useTranslation();
	const [text, setText] = useState("");
	const searchableText = text.trim().toLowerCase();
	const inputRef = useRef<HTMLInputElement>(null);

	//Filtering the suggestions
	let suggestions: AssetCategorization[];
	const availableAdditions: AssetCategory[] = [];
	if (searchableText.length === 0) {
		suggestions = props.categorization.slice(0, autocompleteLimit);
	} else {
		suggestions = props.categorization
			.filter((suggestion) =>
				suggestion.group
					.trim()
					.toLowerCase()
					.startsWith(searchableText),
			)
			.slice(0, 3);
		for (const assetCategory of Object.values(AssetCategory)) {
			if (
				!props.categorization.find(
					(suggestion) =>
						suggestion.category === assetCategory &&
						suggestion.group.trim().toLowerCase() ===
							searchableText,
				)
			) {
				availableAdditions.push(assetCategory);
			}
		}
	}

	function clearInput() {
		if (props.disabled) return;
		setText("");
		props.onChange(undefined);
		setTimeout(() => inputRef.current!.focus(), 0);
	}

	return (
		<div className={styles.container}>
			<div className={styles.label}>
				<span className={styles.labelText}>
					{t("elements.categorization.label")}
				</span>
				<InfoTooltip>
					{t("elements.categorization.tooltip")}
				</InfoTooltip>
			</div>
			<VelavuAutocomplete
				ref={inputRef}
				containerProps={props.containerProps}
				disabled={props.disabled}
				inputProps={{
					type: "text",
					placeholder: t("elements.categorization.placeholder"),
					value: text,
					onChange: (event) => setText(event.target.value),
					disabled: props.disabled,
					...props.inputProps,
				}}
				contentOverride={
					!props.value ? undefined : (
						<div
							style={{
								cursor: props.disabled ? "default" : "pointer",
								flex: 1,
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
							}}
							onClick={clearInput}
						>
							<CategoryTag category={props.value.category} />
							<div style={{ width: 8 }} />
							<GroupTag
								categorization={{
									category: props.value.category,
									group: props.value.group,
								}}
							/>
							<div style={{ flex: 1 }} />
							{!props.disabled && (
								<IconClose
									color={whitelabel.palette.secondary500}
									size={16}
								/>
							)}
						</div>
					)
				}
				disableSuggestions={!!props.value || !!props.disabled}
			>
				{suggestions.length > 0 && (
					<>
						<AutocompleteDivider />
						{suggestions.map((suggestion) => (
							<AutocompleteEntry
								key={`${suggestion.category} ${suggestion.group}`}
								icon={
									<IconArrowForward
										color={whitelabel.palette.secondary500}
										size={16}
									/>
								}
								onClick={() => props.onChange(suggestion)}
							>
								<CategoryTag category={suggestion.category} />
								<div style={{ width: 8 }} />
								<GroupTag categorization={suggestion} />
							</AutocompleteEntry>
						))}
					</>
				)}
				{availableAdditions.length > 0 && (
					<>
						<AutocompleteDivider />
						{availableAdditions.map((addition) => {
							const categorization: AssetCategorization = {
								category: addition,
								group: text,
							};

							return (
								<AutocompleteEntry
									key={`addition ${addition}`}
									icon={
										<IconAdd
											color={
												whitelabel.palette.secondary500
											}
											size={16}
										/>
									}
									onClick={() =>
										props.onChange(categorization)
									}
									hint={t(
										"elements.categorization.add_hint",
										{
											text,
											category: mapCategoryName(
												addition,
												t,
											),
										},
									)}
								>
									<CategoryTag category={addition} />
									<div style={{ width: 8 }} />
									<GroupTag categorization={categorization} />
								</AutocompleteEntry>
							);
						})}
					</>
				)}
			</VelavuAutocomplete>
		</div>
	);
}
